import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarTaskLogsApi } from "../../http/carTaskLogs/api";
import { CarTaskLog } from "../../http/carTaskLogs/types";
import { getTaskStateColor, getTaskStateText } from "../../http/carTasks/types";
import { formatTimeStamp } from "../../types/utils";
import ModalDialog from "../ui/ModalDialog";
import { State } from "../ui/State";
import Table from "../ui/Table";

interface CarTasksHistoryProps {
  carTaskId: string;
  taskName: string;
  onClose: () => void;
}

const CarTasksHistory: FC<CarTasksHistoryProps> = ({ carTaskId, taskName, onClose }) => {
  const filters = useListFilters({
    isSearchable: true,
  });
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const i18n = useI18n();
  const carTaskLogsApi = useCarTaskLogsApi();

  const result = carTaskLogsApi.useCarTaskHistory(aborter.current.signal, carTaskId);
  const [data] = useDataInitializer(result.data, result.error);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
  }, [result.error]);

  return (
    <ModalDialog onClose={onClose} title={`${i18n.t.common.history}: ${taskName}`}>
      <Table<CarTaskLog>
        filters={filters}
        head={
          <>
            <th>{i18n.t.common.state}</th>
            <th align="left">{i18n.t.common.when}</th>
            <th align="left">{i18n.t.common.by}</th>
          </>
        }
        renderRow={(item) => (
          <>
            <td>
              <State
                text={item.state ? getTaskStateText(i18n.t, item.state) : getTaskStateText(i18n.t, undefined)}
                color={getTaskStateColor(item.state)}
              />
            </td>
            <td>{!!item.createdAt && formatTimeStamp(item.createdAt, i18n.language)}</td>
            <td>{item.author?.fullName}</td>
          </>
        )}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />
    </ModalDialog>
  );
};

export default CarTasksHistory;
